import { useEffect, useState } from 'react'
import { FiX, FiSave, FiCheck, FiArrowUpRight, FiAlertCircle, FiLoader, FiEye, FiEdit2 } from 'react-icons/fi'
import toast from 'react-hot-toast'
import MarkdownContent from '../../components/blog/shortcodes'

// Editor del post en drawer lateral. Usado desde BlogAdmin.
// Modos:
//   - mode='create': abre con campos vacíos. Tras guardar, llama onSaved con el slug.
//   - mode='edit': carga el post de la API por slug.
//
// Soporta tres acciones:
//   - Guardar (POST/PUT, sin cambiar estado)
//   - Publicar (PUT con estado='published')
//   - Despublicar (PUT con estado='draft')

const TABS = [
  { id: 'editor', label: 'Editor', icon: FiEdit2 },
  { id: 'preview', label: 'Preview', icon: FiEye },
]

const fetchAuth = (url, options = {}) => {
  const token = localStorage.getItem('token')
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  })
}

export default function BlogAdminEditor({ mode, slug, onClose, onSaved, categorias }) {
  const [tab, setTab] = useState('editor')
  const [loading, setLoading] = useState(mode === 'edit')
  const [saving, setSaving] = useState(false)
  const [post, setPost] = useState({
    slug: '',
    titulo: '',
    descripcion: '',
    excerpt: '',
    categoria_slug: categorias[0]?.slug || '',
    tags: '',
    contenido_md: '',
    imagen_portada: '',
    estado: 'draft',
    fecha_publicacion: '',
  })

  useEffect(() => {
    if (mode !== 'edit' || !slug) return
    setLoading(true)
    fetchAuth(`/api/blog/admin/posts/${encodeURIComponent(slug)}`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => {
        const p = data.post
        setPost({
          slug: p.slug,
          titulo: p.titulo,
          descripcion: p.descripcion,
          excerpt: p.excerpt,
          categoria_slug: p.categoria_slug || categorias[0]?.slug || '',
          tags: (p.tags || []).join(', '),
          contenido_md: p.contenido_md || '',
          imagen_portada: p.imagen_portada || '',
          estado: p.estado,
          fecha_publicacion: p.fecha_publicacion || '',
        })
      })
      .catch((e) => toast.error(`No se pudo cargar el post: ${e.message}`))
      .finally(() => setLoading(false))
  }, [mode, slug, categorias])

  const setField = (field) => (e) => {
    setPost((p) => ({ ...p, [field]: e.target.value }))
  }

  const buildPayload = (overrideEstado) => ({
    slug: post.slug,
    titulo: post.titulo,
    descripcion: post.descripcion,
    excerpt: post.excerpt,
    categoria_slug: post.categoria_slug,
    tags: post.tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 8),
    contenido_md: post.contenido_md,
    imagen_portada: post.imagen_portada || null,
    estado: overrideEstado || post.estado,
    fecha_publicacion: post.fecha_publicacion || undefined,
  })

  const guardar = async (overrideEstado) => {
    setSaving(true)
    try {
      const url = mode === 'create'
        ? '/api/blog/posts'
        : `/api/blog/posts/${encodeURIComponent(post.slug)}`
      const method = mode === 'create' ? 'POST' : 'PUT'
      const res = await fetchAuth(url, { method, body: JSON.stringify(buildPayload(overrideEstado)) })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const detalle = Array.isArray(data.detalle) ? data.detalle.join(' · ') : data.error || 'Error guardando'
        toast.error(detalle, { duration: 8000 })
        return
      }
      toast.success(overrideEstado === 'published' ? 'Publicado' : 'Guardado')
      if (onSaved) onSaved(post.slug)
      if (mode === 'create' && onClose) onClose()
    } catch (e) {
      toast.error(`Error de red: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex">
        <div className="flex-1 bg-black/40" onClick={onClose} />
        <div className="w-full max-w-5xl bg-white shadow-2xl flex items-center justify-center">
          <FiLoader className="h-7 w-7 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-5xl bg-white shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {mode === 'create' ? 'Nuevo post' : `Editando: ${post.slug}`}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Estado: <span className={post.estado === 'published' ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>{post.estado}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-gray-200">
          {TABS.map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            )
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {tab === 'editor' ? (
            <div className="p-6 space-y-4 max-w-3xl">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Slug" required>
                  <input
                    type="text" value={post.slug} onChange={setField('slug')}
                    disabled={mode === 'edit'}
                    placeholder="mi-post-en-kebab-case"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-mono disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </Field>
                <Field label="Categoría" required>
                  <select value={post.categoria_slug} onChange={setField('categoria_slug')}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm">
                    {categorias.map((c) => <option key={c.slug} value={c.slug}>{c.nombre}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Título" required>
                <input type="text" value={post.titulo} onChange={setField('titulo')}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" />
              </Field>
              <Field label="Meta descripción (SEO, hasta 200 chars)" required hint={`${post.descripcion.length}/200`}>
                <input type="text" value={post.descripcion} onChange={setField('descripcion')} maxLength={200}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" />
              </Field>
              <Field label="Excerpt (resumen para listados)" required>
                <textarea rows={2} value={post.excerpt} onChange={setField('excerpt')}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Imagen de portada">
                  <input type="text" value={post.imagen_portada} onChange={setField('imagen_portada')}
                    placeholder="/img/blog/mi-imagen.jpg"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" />
                </Field>
                <Field label="Fecha de publicación (YYYY-MM-DD)">
                  <input type="text" value={post.fecha_publicacion} onChange={setField('fecha_publicacion')}
                    placeholder="2026-05-06"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-mono" />
                </Field>
              </div>
              <Field label="Tags (separados por coma, máx 8)">
                <input type="text" value={post.tags} onChange={setField('tags')}
                  placeholder="rgpd, geolocalización, normativa"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" />
              </Field>
              <Field label="Contenido (Markdown + shortcodes)" hint="Ver shortcodes en /api/blog/shortcodes">
                <textarea rows={20} value={post.contenido_md} onChange={setField('contenido_md')}
                  spellCheck={false}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-mono leading-relaxed" />
              </Field>
            </div>
          ) : (
            <div className="px-6 py-8 max-w-4xl">
              <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-blue-600 prose-strong:text-gray-900">
                <h1>{post.titulo || 'Sin título'}</h1>
                <p className="lead">{post.excerpt}</p>
                <MarkdownContent contenido={post.contenido_md} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3 bg-gray-50">
          <div className="text-xs text-gray-500 inline-flex items-center gap-1.5">
            <FiAlertCircle className="h-3.5 w-3.5" />
            Si publicas, el post será visible en /blog/{post.slug || '...'}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancelar
            </button>
            <button onClick={() => guardar()} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg disabled:opacity-50">
              {saving ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSave className="h-4 w-4" />}
              Guardar borrador
            </button>
            <button onClick={() => guardar('published')} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
              {saving ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiCheck className="h-4 w-4" />}
              Publicar
            </button>
            {mode === 'edit' && post.slug && (
              <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900">
                <FiArrowUpRight className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </span>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      {children}
    </label>
  )
}
